import { useQuery } from '@apollo/client';
import { TableProps } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { createSortDescription } from 'Components/Table/createColumns';
import { useState } from 'react';
import { GET_SYSTEM_ROLES } from 'Services/Api/Roles/Queries';
import {
	GetSystemRolesRequest,
	GetSystemRolesResponse,
	SystemRole
} from 'Services/Api/Roles/Types';
import useColumns, { TableFilterValues } from './columns';
import { PermissionSectionData, RoleSubmitPayload } from './components/PermissionList';
import { getTenantRoles } from './service/index.Service';

const DUMMY_PERMISSIONS: PermissionSectionData[] = [
	{
		key: 'dashboard',
		label: 'Dashboard',
		groups: [
			{
				items: [
					{ key: 'view_training', label: 'View Training', checked: false },
					{ key: 'view_results', label: 'View My Results', checked: false },
					{ key: 'view_overview', label: 'View Overview', checked: false }
				]
			}
		]
	},
	{
		key: 'settings',
		label: 'Settings',
		groups: [
			{
				title: 'Users',
				items: [
					{ key: 'view_users', label: 'View Users', checked: true, disabled: true },
					{ key: 'add_users', label: 'Add Users', checked: true },
					{ key: 'edit_users', label: 'Edit Users', checked: true },
					{ key: 'delete_users', label: 'Delete Users', checked: false }
				]
			}
		]
	}
];

const DUMMY_ROLES: Record<string, { id: string; role: string; description: string }> = {
	'45': {
		id: '45',
		role: 'Admin',
		description: 'Full access to all system features and settings'
	}
};

export function usePermissionList() {
	return {
		permissionList: DUMMY_PERMISSIONS,
		loading: false
	};
}

export function useRoleDetail(roleId: string) {
	const [loading] = useState(false);
	const role = DUMMY_ROLES[roleId] ?? {
		id: roleId,
		role: 'Unknown Role',
		description: ''
	};

	return {
		role,
		permissionList: DUMMY_PERMISSIONS,
		loading,
		refetch: () => Promise.resolve(),
	};
}

export function useCreateRole() {
	const [loading, setLoading] = useState(false);

	const createRole = async (values: RoleSubmitPayload) => {
		setLoading(true);
		await new Promise((resolve) => setTimeout(resolve, 300));
		setLoading(false);
	};

	return [createRole, { loading }] as const;
}

export function useUpdateRole() {
	const [loading, setLoading] = useState(false);

	const updateRole = async (roleId: string, values: RoleSubmitPayload) => {
		setLoading(true);
		await new Promise((resolve) => setTimeout(resolve, 300)); // fake latency
		setLoading(false);
	};

	return [updateRole, { loading }] as const;
}

export const GET_ROLES_INITIAL_PARAMS: GetSystemRolesRequest = {
	sort: createSortDescription({
		columnKey: 'roleName',
		order: 'ascend'
	}),
	filters: {}
};

const mapTenantRoleResponse = (items: any[] = []): SystemRole[] =>
	items.map((item) => ({
		id: item.id,
		roleName: item.roleName,
		description: item.description,
		type: item.type,
		usersCount: Number(item.usersCount ?? 0),
		updatedDate: item.updatedDate
	}));

export const useRoles = () => {
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [selectedTenantId, setSelectedTenantId] = useState<number | string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [dataSource, setDataSource] = useState<SystemRole[]>([]);
	const [params, setParams] = useState<GetSystemRolesRequest>(
		GET_ROLES_INITIAL_PARAMS
	);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [roleName, setRoleName] = useState("");
	const [total, setTotal] = useState(0);
	const result = useQuery<GetSystemRolesResponse, GetSystemRolesRequest>(
		GET_SYSTEM_ROLES,
		{
			variables: GET_ROLES_INITIAL_PARAMS,
			notifyOnNetworkStatusChange: true,
			skip: !selectedTenantId
		}
	);

	const refetch = async (
		values: GetSystemRolesRequest = GET_ROLES_INITIAL_PARAMS, tenantId?: string | number,
		currentPage: number = page,
		currentPageSize: number = pageSize
	) => {
		setParams(values);

		const targetTenantId = tenantId ?? selectedTenantId;
		const hasTenant = targetTenantId !== undefined && targetTenantId !== '';
		
		if (hasTenant) {
			setLoading(true);
			try {
				const sort = values.sort || 'roleName:asc';
				const [sortBy, order] = sort.split(':');

				const response = await getTenantRoles(targetTenantId, {
					dropdown: false,
					allTenants: targetTenantId === 0,
					role: values?.filters?.roleName ?? '',
					sortBy: sortBy || 'roleName',
					sortOrder: order?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC',
					page: currentPage,
					limit: currentPageSize
				});
				setDataSource(mapTenantRoleResponse(response?.data));
				setTotal(response.total);
				setPage(response.page);
				setPageSize(response.limit);
			} finally {
				setLoading(false);
			}
		}
	};

	const onTenantChange = async (tenantId: string | number) => {
		setSelectedTenantId(tenantId);
		setLoading(true);
		try {
			const values: GetSystemRolesRequest = GET_ROLES_INITIAL_PARAMS
			const variables: GetSystemRolesRequest = {
				...values,
				filters: {
					...values.filters,
				}
			};
			await refetch(variables, tenantId);
		} finally {
			setLoading(false);
		}
	};


	const onTableChange: TableProps<SystemRole>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sort = createSortDescription<SystemRole>(
			sorter as SorterResult<SystemRole>
		);

		const variables: GetSystemRolesRequest = {
			sort,
			filters: {
				roleName:roleName
			}
		};

		refetch(
			variables,
			undefined,
			pagination.current ?? 1,
			pagination.pageSize ?? 10
		);
	};

	const columns = useColumns({
		params
	});

	const onPaginationChange = (
		page: number,
		pageSize?: number
	) => {
		refetch(
			params,
			undefined,
			page,
			pageSize ?? 10
		);
	};

	return {
		result,
		dataSource,
		columns,
		onTableChange,
		selectedRowKeys,
		setSelectedRowKeys,
		selectedTenantId,
		setSelectedTenantId,
		onTenantChange,
		refetch,
		loading,
		page,
		pageSize,
		total,
		params,
		onPaginationChange,
		roleName,
		setRoleName
	};
};
